import { setDesiredReturnDates, setPickUpAddress } from "@App/state/reducers/vehicleReturn";
import { selectDesiredReturnDates, selectPickUpAddress } from "@App/state/selectors/vehicleReturn";
import { addBusinessDays, isWeekend } from "@App/utils/dates";
import { isSameDay } from "date-fns";
import { isHoliday, type Region } from "feiertagejs";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";

export function useReturnDetails(onSubmit: () => void) {
  const dispatch = useDispatch();

  const pickUpAddress = useSelector(selectPickUpAddress);
  const desiredReturnDates = useSelector(selectDesiredReturnDates);

  const [selectedDates, setSelectedDates] = useState({
    date1: desiredReturnDates.date1,
    date2: desiredReturnDates.date2,
    date3: desiredReturnDates.date3,
  });

  const createSetter = (key: keyof typeof selectedDates) => (date: Date | null) =>
    setSelectedDates((prev) => ({ ...prev, [key]: date }));

  const selectedDatesSetters = {
    date1: createSetter("date1"),
    date2: createSetter("date2"),
    date3: createSetter("date3"),
  };

  const [showAddressModal, setShowAddressModal] = useState(false);

  function openAddressModal() {
    setShowAddressModal(true);
  }

  function closeAddressModal() {
    setShowAddressModal(false);
  }

  const [costAccept, setCostAccept] = useState(false);
  const [region] = useState<Region>("BUND");

  const isSelectedDay = (date: Date) => {
    return (
      (selectedDates.date1 && isSameDay(date, selectedDates.date1)) ||
      (selectedDates.date2 && isSameDay(date, selectedDates.date2)) ||
      (selectedDates.date3 && isSameDay(date, selectedDates.date3))
    );
  };

  const isDateDisabled = (date: Date) => {
    return isSelectedDay(date) || isWeekend(date) || isHoliday(date, region);
  };

  const minDate = addBusinessDays(new Date(), 10, region);
  const maxDate = addBusinessDays(new Date(), 40, region);

  const changeAddress = (
    name: string,
    phone: string,
    street: string,
    city: string,
    postcode: string,
    email: string,
  ) => {
    dispatch(
      setPickUpAddress({
        name: name,
        street: street,
        postcode: postcode,
        city: city,
        phone: phone,
        email: email,
      }),
    );
    setShowAddressModal(false);
  };

  // The contract data occasionally carries an unusable email (a placeholder, or two
  // addresses in one field), which the pickup mutation rejects. It is corrected in the
  // address modal, so the customer is never stuck with what the backend delivered.
  const isEmailValid = z.email().safeParse(pickUpAddress.email).success;

  const isContactDataComplete =
    pickUpAddress.name &&
    pickUpAddress.street &&
    pickUpAddress.postcode &&
    pickUpAddress.city &&
    pickUpAddress.phone;

  const isButtonDisabled =
    !selectedDates.date1 ||
    !selectedDates.date2 ||
    !selectedDates.date3 ||
    !costAccept ||
    !isContactDataComplete ||
    !isEmailValid;

  const submit = () => {
    dispatch(
      setDesiredReturnDates({
        date1: selectedDates.date1,
        date2: selectedDates.date2,
        date3: selectedDates.date3,
      }),
    );
    onSubmit();
  };

  return {
    pickUpAddress,
    changeAddress,
    openAddressModal,
    closeAddressModal,
    showAddressModal,
    isButtonDisabled,
    submit,
    costAccept,
    setCostAccept,
    isEmailValid,
    dates: {
      minDate,
      maxDate,
      selectedDates,
      isDateDisabled,
      selectedDatesSetters,
    },
    isContactDataComplete,
  };
}
